import React, { useMemo } from 'react';

import type { CellData, CellFormulaInfo, HotComputedCellValue } from '../../../../common/table/TableTypes';
import hasAnyTiptapMarks from '../../../../common/tiptap/utils/jsonContentUtils/hasAnyTiptapMarks';
import getTiptapNodeTextContent from '../../../../common/tiptap/utils/jsonContentUtils/getTiptapNodeTextContent';
import { hasAnyLinks } from '../../../../common/utils/getLinksFromText';

import { getCellTextAlignment } from '../utils/dataUtils/cell/tableCellDisplayUtils';
import { getCellRenderValue } from '../utils/dataUtils/cell/tableCellRenderValueUtils';
import { getTiptapContentFromPlainText } from './tiptapCellEditorContentUtils';
import PlainTextCellRenderer from './TableCellPlainTextRenderer';
import TiptapCellRenderer from './TiptapCellRenderer';

type Props = {
    elementId: string;
    hotComputedCellValue: HotComputedCellValue;
    cellData: CellData;
    locale: string;
    filterQuery?: string;
    cellFormulaInfo?: CellFormulaInfo;
};

const TiptapCellRendererComponent: React.FC<Props> = ({
    hotComputedCellValue,
    cellData,
    filterQuery,
    cellFormulaInfo,
    locale,
    elementId,
}) => {
    const plainText = useMemo(() => {
        if (cellData.tiptapContent) return getTiptapNodeTextContent(cellData.tiptapContent);

        return getCellRenderValue(cellData, locale, hotComputedCellValue, cellFormulaInfo) || '';
    }, [cellData, locale, hotComputedCellValue, cellFormulaInfo]);

    const tiptapContent = useMemo(() => {
        const cellHasFilterQuery = !!filterQuery && plainText.toLowerCase().includes(filterQuery.toLowerCase());
        const shouldRenderTiptapEditor =
            cellHasFilterQuery || hasAnyLinks(plainText) || hasAnyTiptapMarks(cellData.tiptapContent);
        if (!shouldRenderTiptapEditor) return null;

        if (cellData.tiptapContent) return cellData.tiptapContent;

        return getTiptapContentFromPlainText(plainText, cellData.textStyle);
    }, [cellData, plainText, filterQuery]);

    const wrapperCssStyle = useMemo<React.CSSProperties>(
        () => ({ textAlign: getCellTextAlignment(cellData, locale, hotComputedCellValue) }),
        [cellData, locale, hotComputedCellValue],
    );

    if (!tiptapContent) {
        return <PlainTextCellRenderer plainText={plainText} textStyle={cellData.textStyle} style={wrapperCssStyle} />;
    }

    return <TiptapCellRenderer elementId={elementId} textContent={tiptapContent} style={wrapperCssStyle} />;
};

export default TiptapCellRendererComponent;
